// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import * as validate from '../validation';
import { FieldValidator, enumValidator } from '../validation';
import { ApiEnvironment } from '../vsCodeEduClientExt';
import * as assert from './assert';

export const VSCodeEduDirectoryName = '/VSCode Edu';
export const VSCodeEduDirectoryMatcherRegExp = new RegExp(`^[\\/]?${VSCodeEduDirectoryName}`);

// Possible values for the 'workspace-scheme' URL query string parameter.
export enum WorkspaceScheme {
    Workspace = 'vscode-edu-workspace',
    Internal = 'vscode-edu-internal',
}

// Represents the query string parameters of a vscode.dev/edu URL.
export type VSCodeUrlParams = Partial<{
    [key in keyof Omit<
        VSCodeUrl,
        'toString' | 'forCourse' | 'forProject' | 'forSharedProject' | 'forCurriculum'
    >]: VSCodeUrl[key];
}>;

// Represents a vscode.dev/edu URL with strongly-typed validated query string parameters.
export class VSCodeUrl {
    private static validateWorkspaceScheme = enumValidator(Object.values(WorkspaceScheme));
    private readonly url: URL;

    // Creates a new vscode.dev URL with the specified parameters.
    constructor(url: ConstructorParameters<typeof URL>[0] | ApiEnvironment, params?: VSCodeUrlParams) {
        switch (url) {
            case ApiEnvironment.Production:
                this.url = new URL('https://vscode.dev/edu');
                break;
            case ApiEnvironment.Beta:
                this.url = new URL('https://vscode.dev/edu?env=beta');
                break;
            case ApiEnvironment.PreProduction:
            case ApiEnvironment.LocalHost:
                this.url = new URL('https://insiders.vscode.dev/edu?env=ppe');
                break;
            default:
                this.url = new URL(url);
                break;
        }

        if (params !== undefined) {
            Object.assign(this, params);
        }
    }

    // Creates vscode.dev URL for the specified course.
    public static forCourse(environment: ApiEnvironment, courseId: string, params?: VSCodeUrlParams): VSCodeUrl {
        return new VSCodeUrl(environment, {
            courseId,
            workspaceScheme: WorkspaceScheme.Workspace,
            ...params,
        });
    }

    // Creates vscode.dev URL for the specified project.
    public static forProject(environment: ApiEnvironment, projectId: string, params?: VSCodeUrlParams): VSCodeUrl {
        return new VSCodeUrl(environment, {
            projectId,
            profile: 'project',
            workspaceScheme: WorkspaceScheme.Workspace,
            workspacePath: VSCodeEduDirectoryName,
            ...params,
        });
    }

    // Creates vscode.dev URL for the specified shared project link.
    public static forSharedProject(
        environment: ApiEnvironment,
        sharedLinkId: string,
        params?: VSCodeUrlParams
    ): VSCodeUrl {
        return new VSCodeUrl(environment, {
            sharedLinkId,
            profile: 'project',
            workspaceScheme: WorkspaceScheme.Workspace,
            workspacePath: VSCodeEduDirectoryName,
            ...params,
        });
    }

    // Creates vscode.dev URL for the specified curriculum.
    public static forCurriculum(
        environment: ApiEnvironment,
        curriculumId: string,
        githubRepo: string,
        params?: VSCodeUrlParams
    ): VSCodeUrl {
        return new VSCodeUrl(environment, {
            curriculumId,
            profile: 'curriculum',
            workspaceScheme: WorkspaceScheme.Workspace,
            githubRepo,
            ...params,
        });
    }

    // Returns string representation of the URL.
    public toString(): string {
        const value = this.url.toString();
        return value.endsWith('?') ? value.slice(0, -1) : value;
    }

    public get environment(): ApiEnvironment {
        switch (this.getParam('env')?.toLowerCase()) {
            case 'ppe':
                return ApiEnvironment.PreProduction;
            case 'beta':
                return ApiEnvironment.Beta;
            default:
                return ApiEnvironment.Production;
        }
    }
    public set environment(value: ApiEnvironment) {
        switch (value) {
            case ApiEnvironment.PreProduction:
            case ApiEnvironment.LocalHost:
                this.url.hostname = 'insiders.vscode.dev';
                this.url.searchParams.set('env', 'ppe');
                break;
            case ApiEnvironment.Beta:
                this.url.hostname = 'vscode.dev';
                this.url.searchParams.set('env', 'beta');
                break;
            default:
                this.url.hostname = 'vscode.dev';
                this.url.searchParams.delete('env');
                break;
        }
    }

    public get extensionVersion(): string | undefined {
        return this.getParam('extension');
    }
    public set extensionVersion(value: string | undefined) {
        this.setParam('extension', value);
    }

    public get homePageVersion(): string | undefined {
        return this.getParam('homePageVersion');
    }
    public set homePageVersion(value: string | undefined) {
        this.setParam('homePageVersion', value);
    }

    public get courseId(): string | undefined {
        return this.getParam('courseId', validate.uniqueIdPart);
    }
    public set courseId(value: string | undefined) {
        this.setParam('courseId', value, validate.uniqueIdPart);
    }

    public get projectId(): string | undefined {
        return this.getParam('projectId', validate.uniqueIdPart);
    }
    public set projectId(value: string | undefined) {
        this.setParam('projectId', value, validate.uniqueIdPart);
    }

    public get sharedLinkId(): string | undefined {
        return this.getParam('sharedLinkId', validate.sharedLinkId);
    }
    public set sharedLinkId(value: string | undefined) {
        this.setParam('sharedLinkId', value, validate.sharedLinkId);
    }

    public get profile(): string | undefined {
        return this.getParam('profile', validate.profileName);
    }
    public set profile(value: string | undefined) {
        this.setParam('profile', value, validate.profileName);
    }

    public get workspaceScheme(): WorkspaceScheme | undefined {
        const value = this.getParam('workspace-scheme');
        return VSCodeUrl.validateWorkspaceScheme(value, 'value') === undefined ? (value as WorkspaceScheme) : undefined;
    }
    public set workspaceScheme(value: WorkspaceScheme | undefined) {
        this.setParam('workspace-scheme', value, VSCodeUrl.validateWorkspaceScheme);
    }

    public get workspacePath(): string | undefined {
        return this.getParam('workspace-path');
    }
    public set workspacePath(value: string | undefined) {
        this.setParam('workspace-path', value);
    }

    public get accountId() {
        return this.getHashParam('account-id', validate.uuid);
    }
    public set accountId(value: string | undefined) {
        this.setHashParam('account-id', value, validate.uuid);
    }

    public get selectCourseNode() {
        return this.getHashParam('select-course-node');
    }
    public set selectCourseNode(value: string | undefined) {
        this.setHashParam('select-course-node', value);
    }

    public get codeChallenge() {
        return this.getParam('codeChallenge', validate.codeVerifier);
    }
    public set codeChallenge(value: string | undefined) {
        this.setParam('codeChallenge', value, validate.codeVerifier);
    }

    public get otac() {
        return this.getParam('otac', validate.otac);
    }
    public set otac(value: string | undefined) {
        this.setParam('otac', value, validate.otac);
    }

    public get redirectUri() {
        return this.getParam('redirectUri', validate.absoluteUrl);
    }
    public set redirectUri(value: string | undefined) {
        this.setParam('redirectUri', value, validate.absoluteUrl);
    }

    public get region() {
        return this.getParam('region');
    }
    public set region(value: string | undefined) {
        this.setParam('region', value);
    }

    public get requestId() {
        return this.getParam('requestId', validate.uuid);
    }
    public set requestId(value: string | undefined) {
        this.setParam('requestId', value, validate.uuid);
    }

    public get signOut() {
        return this.getParam('sign-out');
    }
    public set signOut(value: string | undefined) {
        this.setParam('sign-out', value);
    }

    public get curriculumId() {
        return this.getParam('curriculumId', validate.uniqueIdPart);
    }
    public set curriculumId(value: string | undefined) {
        this.setParam('curriculumId', value, validate.uniqueIdPart);
    }

    public get githubRepo() {
        return this.getParam('githubRepo', validate.githubRepo);
    }
    public set githubRepo(value: string | undefined) {
        this.setParam('githubRepo', value, validate.githubRepo);
    }

    // Retrieves and validates a query string parameter.
    private getParam(param: string, validator?: FieldValidator): string | undefined {
        const value = this.url.searchParams.get(param);
        if (value === null || value.length === 0) {
            return undefined;
        } else if (validator?.(value, 'param') !== undefined) {
            return undefined;
        } else {
            return value;
        }
    }

    // Validates, then sets a query string parameter.
    private setParam(param: string, value?: string, validator?: FieldValidator): void {
        if (value === undefined || value.length === 0) {
            this.url.searchParams.delete(param);
        } else {
            assert.isValid(validator?.(value, param));
            this.url.searchParams.set(param, value);
        }
    }

    // Retrieves and validates a hash parameter.
    private getHashParam(param: string, validator?: FieldValidator): string | undefined {
        const hash = new URLSearchParams(this.url.hash.slice(1));
        const value = hash.get(param);
        if (value === null || value.length === 0) {
            return undefined;
        } else if (validator?.(value, 'param') !== undefined) {
            return undefined;
        } else {
            return value;
        }
    }

    // Validates, then sets a hash parameter.
    private setHashParam(param: string, value?: string, validator?: FieldValidator): void {
        const hash = new URLSearchParams(this.url.hash.slice(1));
        if (value === undefined || value.length === 0) {
            hash.delete(param);
        } else {
            assert.isValid(validator?.(value, param));
            hash.set(param, value);
        }
        this.url.hash = hash.toString();
    }
}
